import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { getMyOrgs, switchOrg } from '../api/auth'
import { listOrganizations } from '../api/organizations'
import { useAuth } from '../hooks/useAuth'
import Modal from './Modal'

interface OrgSwitchOption {
  slug: string
  name: string
}

interface OrgSwitcherModalProps {
  isOpen: boolean
  onClose: () => void
  currentSlug?: string
}

/**
 * In-app org switcher, visually matching the login-time chooser
 * (SelectOrgPage: "Choose your organization"). Lists the orgs the caller's own
 * identity belongs to (GET /auth/my-orgs); superadmins instead get every active
 * org (GET /admin/organizations) so they can hop into any org to inspect it.
 * The choice is exchanged for fresh org-scoped tokens via POST /auth/switch-org,
 * then hard-navigates to /{slug}/dashboard so the router basename resets.
 */
export default function OrgSwitcherModal({ isOpen, onClose, currentSlug }: OrgSwitcherModalProps) {
  const { user } = useAuth()
  const isSuperadmin = user?.role === 'superadmin'
  const [orgs, setOrgs] = useState<OrgSwitchOption[] | null>(null)
  const [selectedSlug, setSelectedSlug] = useState(currentSlug ?? '')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setSelectedSlug(currentSlug ?? '')
    setOrgs(null)
    let cancelled = false
    void (async () => {
      try {
        if (isSuperadmin) {
          const all = await listOrganizations()
          if (cancelled) return
          setOrgs(
            all.filter((org) => org.isActive).map((org) => ({ slug: org.slug, name: org.name }))
          )
        } else {
          const data = await getMyOrgs()
          if (cancelled) return
          setOrgs(data.orgs)
        }
      } catch {
        if (cancelled) return
        setOrgs([])
        toast.error('Failed to load organizations')
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, currentSlug, isSuperadmin])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedSlug || isSubmitting || selectedSlug === currentSlug) {
      if (selectedSlug === currentSlug) onClose()
      return
    }
    setIsSubmitting(true)
    try {
      const res = await switchOrg(selectedSlug)
      if (!res.accessToken || !res.refreshToken || !res.orgSlug) {
        throw new Error('Organization switch failed')
      }
      localStorage.setItem(`accessToken:${res.orgSlug}`, res.accessToken)
      localStorage.setItem(`refreshToken:${res.orgSlug}`, res.refreshToken)
      localStorage.removeItem('token')
      window.location.href = `/${res.orgSlug}/dashboard`
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Organization switch failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Choose your organization">
      {orgs === null ? (
        <div className="flex justify-center py-6">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
        </div>
      ) : orgs.length === 0 ? (
        <p className="text-sm text-harbor-text-secondary">No organizations found.</p>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="org-switcher-select"
              className="block text-sm font-medium text-harbor-text-secondary mb-1"
            >
              Organization
            </label>
            <select
              id="org-switcher-select"
              value={selectedSlug}
              onChange={(e) => setSelectedSlug(e.target.value)}
              required
              disabled={isSubmitting}
              className="w-full px-4 py-3 bg-harbor-surface border border-harbor-border rounded-lg text-harbor-text focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent disabled:opacity-50"
            >
              <option value="" disabled>
                Select an organization…
              </option>
              {orgs.map((org) => (
                <option key={org.slug} value={org.slug}>
                  {org.name} ({org.slug}){org.slug === currentSlug ? ' — current' : ''}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={isSubmitting || !selectedSlug}
            className="w-full py-3 px-4 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-harbor-ring-offset disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSubmitting ? 'Switching…' : 'Switch organization'}
          </button>
        </form>
      )}
    </Modal>
  )
}
